/** @file Stage table whose stages reuse cached results by document identity (what recompile runs). */
import { invariant } from '@pose2d/std/invariant';
import type { Doc } from '@pose2d/format/types';
import type { CompileStages } from '../compile/compileStages';
import { cachedAnimations } from './cachedAnimations';
import { canonicalMap } from './canonicalMap';
import type { CompileCache } from './compileCache';
import { hierarchyKey } from './hierarchyKey';
import { memoStage } from './memoStage';

type CompiledAttachments = ReturnType<CompileStages['attachments']>;

/** Animation names in document order (directions resolve `<base>@<DIR>` variants by name). */
const animationNames = (doc: Doc): string => Object.values(doc.animations).map((a) => a.name).join('\n');
/** Bone objects of every socket and grip-2 bone (a mount may read its socket bone, e.g. its length). */
const socketBones = (doc: Doc): unknown[] =>
  Object.values(doc.sockets).flatMap((s) => [doc.bones[s.bone], s.grip2Bone === undefined ? null : doc.bones[s.grip2Bone]]);

/**
 * Tripwire for one in-place breach only: a skinned mesh added to a `doc.attachments` table that the cache already
 * holds has no record in the cached result, and attachSkins would skip it silently. Other in-place edits (a mesh
 * turned skinned, new weights or bind poses on the same object) still pass unseen: immutability is the real guard.
 */
function checkSkinnedRecords(doc: Doc, compiled: CompiledAttachments): CompiledAttachments {
  for (const [id, att] of Object.entries(doc.attachments)) {
    if (att.type !== 'mesh' || att.geometry.kind !== 'skinned') continue;
    const record = compiled.attachments[compiled.attachmentIndex.get(id) ?? -1];
    invariant(record?.type === 'mesh', `stale attachments cache: skinned mesh ${id} has no compiled mesh record`);
  }
  return compiled;
}

// Canonical-map roles are `<stage>.<map>`, so two stages fed different maps never evict each other. The keys are
// the G.2 memo table; a stage that starts reading another document table must add it here.
/** Wraps every stage of `direct` with the memo keys of spec §6.10; results equal `direct`'s for equal inputs. */
export function cachedStages(cache: CompileCache, direct: CompileStages): CompileStages {
  const canon = (role: string, map: Map<string, number>): Map<string, number> => canonicalMap(cache.maps, role, map);
  return {
    bones: (doc, index) => memoStage(cache.bones, doc.bones, [doc.sockets], () => direct.bones(doc, index)),
    updateOrder: (doc, index, boneIndex, ikIndex) =>
      memoStage(cache.stages, 'updateOrder', [hierarchyKey(doc.bones), doc.constraints, canon('updateOrder.bone', boneIndex), canon('updateOrder.ik', ikIndex)], () =>
        direct.updateOrder(doc, index, boneIndex, ikIndex)),
    attachments: (doc, index, boneIndex) =>
      checkSkinnedRecords(doc, memoStage(cache.attachments, doc.attachments, [doc.images, doc.sockets, doc.skins, doc.slots, canon('attachments.bone', boneIndex), ...socketBones(doc)], () =>
        direct.attachments(doc, index, boneIndex))),
    skinning: (mesh, boneIndex) => memoStage(cache.skinning, mesh, [canon('skinning.bone', boneIndex)], () => direct.skinning(mesh, boneIndex)),
    sockets: (doc, index, boneIndex) =>
      memoStage(cache.stages, 'sockets', [doc.sockets, doc.bones, doc.attachments, doc.slots, doc.skins, canon('sockets.bone', boneIndex)], () =>
        direct.sockets(doc, index, boneIndex)),
    animations: cachedAnimations(cache, direct.animations),
    directions: (doc, index, maps) =>
      memoStage(
        cache.stages,
        'directions',
        [doc.directions, doc.view.grid, animationNames(doc), canon('directions.skin', maps.skinIndex), canon('directions.animation', maps.animationIndex), canon('directions.slot', maps.slotIndex)],
        () => direct.directions(doc, index, maps),
      ),
  };
}
